import DataQuery from 'data/DataQuery';
import { Customer_ContractDropdown, Customer_Get } from 'data/endpoints';
import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import IAppState from 'infrastructure/store/IAppState';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import BasicInfoSubFormSection from '../basicInfoSubForm/BasicInfoSubFormSection';
import { complaintFormInitValidations, setInvoiceValidation } from './complaintFormValidation';
import { useQuery } from 'data/hooks/useQuery';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import { getQueryParams } from 'features/shared/helpers/queryParams';
import { ContractStatus } from 'features/contracts/enums/contractStatus';
import ContractSelect from 'features/contracts/components/ContractSelect';
import IComplaintFormService from 'features/requirements/services/IComplaintFormService';
import ComplaintTypeDTO from 'features/requirements/dtos/ComplaintTypeDTO';
import ComplaintSubTypeDTO from 'features/requirements/dtos/ComplaintSubTypeDTO';
import ComplaintSelect from 'features/requirements/components/complaintForm/ComplaintSelect';
import { Nullable } from 'features/shared/types/nullable';
import InvoiceSimplifiedDTO from 'features/invoices/dtos/InvoiceSimplifiedDTO';
import { isEmpty } from 'features/shared/helpers/objects';
import PromiseLoadingSpinner from 'features/shared/components/loaders/PromiseLoadingSpinner';
import { ComplaintFormSection_Complaint } from 'features/shared/components/loaders/promiseLoadingAreas';
import { trackPromise } from 'react-promise-tracker';
import { useTranslation } from 'react-i18next';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';

const ComplaintFormSection = () => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const formService = useInjection<IComplaintFormService>(IocTypes.ComplaintFormService);

  // store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;

  // state
  const [complaintTypes, setComplaintTypes] = useState<ComplaintTypeDTO[]>([]);
  const [complaintSubTypes, setComplaintSubTypes] = useState<ComplaintSubTypeDTO[]>([]);
  const [invoices, setInvoices] = useState<InvoiceSimplifiedDTO[]>([]);
  const [selectedContract, setSelectedContract] = useState<ContractDropdownDTO>({});
  const [selectedComplaintType, setSelectedComplaintType] = useState<Nullable<ComplaintTypeDTO>>(null);
  const [selectedComplaintSubType, setSelectedComplaintSubType] = useState<Nullable<ComplaintSubTypeDTO>>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Nullable<InvoiceSimplifiedDTO>>(null);

  // fetch data
  let contractsQuery: DataQuery = {
    url: Customer_ContractDropdown,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: getQueryParams("ContractStatus", [ContractStatus.Effective, ContractStatus.Terminated])
  };
  const { data: contracts } = useQuery<ContractDropdownDTO[]>(contractsQuery, [], [customer]);

  // form hook
  const { values, setFormValue, errors, bindField, bindUncontrolledField, valideteForm, validateFormField, setValidationRule } = useForm({
    initialValidations: complaintFormInitValidations()
  });

  // side effects
  useEffect(() => {
    // set default contract
    if (contracts.length === 1) {
      contractChangeHandler(contracts[0]);
    }
  }, [contracts])

  // helpers
  const isEditableComplaintType = () =>
    formService.isFilledValues(values, ["DESC_TMSMLOUVA"]);
    
  const isEditableComplaintSubType = () =>
    formService.isFilledValues(values, [
      "DESC_TMSMLOUVA",
      "DESC_TMKATEGREKLAM",
    ]);

  const isEditableInvoice = () => {
    if (
      selectedComplaintSubType &&
      !isEmpty(selectedComplaintSubType) &&
      selectedComplaintSubType.invoiceEnable
    ) {
      return true;
    }

    return false;
  }

  // handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (valideteForm()) {
      let result = await createService.createRequirement("REKLAMACE", values);
      openRequirementResultDialog(false, { response: result });
    }
  };

  const contractChangeHandler = async (contract: ContractDropdownDTO | null) => {
    if (contract && validateFormField("DESC_TMSMLOUVA", contract.contractId)) {
      // clear dependent values
      formService.clearFormValues(["DESC_TMKATEGREKLAM", "DESC_TMPODKATREKLAM", "DESC_TMDOKLAD"], setFormValue);
      setSelectedComplaintType(null);
      setSelectedComplaintSubType(null);

      // fetch complaint types
      let result = await trackPromise(formService.fetchComplaintTypes(contract.contractId!), ComplaintFormSection_Complaint);

      // set state
      setComplaintTypes(result); 
      setSelectedContract(contract);

      // set select data to form values
      setFormValue("DESC_TMSMLOUVA", contract.contractId);
    }
  }

  const complaintTypeChangeHandler = async (complaintType: Nullable<ComplaintTypeDTO>) => {
    if (complaintType && validateFormField("DESC_TMKATEGREKLAM", complaintType.complaintTypeId)) {
      // clear dependent values
      formService.clearFormValues(["DESC_TMPODKATREKLAM", "DESC_TMDOKLAD"], setFormValue);
      setSelectedComplaintSubType(null);

      // set state
      setSelectedComplaintType(complaintType);
      setComplaintSubTypes(complaintType.complaintSubTypes ?? []);

      // set select data to form values
      setFormValue("DESC_TMKATEGREKLAM", complaintType.complaintTypeId);
    }
  }

  const complaintSubTypeChangeHandler = async (complaintSubType: Nullable<ComplaintSubTypeDTO>) => {
    if (complaintSubType && validateFormField("DESC_TMPODKATREKLAM", complaintSubType.complaintSubTypeId)) {
      // clear dependent values
      formService.clearFormValues(["DESC_TMDOKLAD"], setFormValue);
      setSelectedInvoice(null);

      // set state
      setSelectedComplaintSubType(complaintSubType);

      // set select data to form values
      setFormValue("DESC_TMPODKATREKLAM", complaintSubType.complaintSubTypeId);

      // fetch invoices
      if (complaintSubType.invoiceEnable) {
        
        let result = await trackPromise(formService.fetchInvoices(selectedContract.contractId!), ComplaintFormSection_Complaint);

        // set state
        setInvoices(result.result);
      }  
      
      // set validation rule for invoice input 
      setInvoiceValidation(setValidationRule, complaintSubType);
    }
  }

  const invoiceChangeHandler = (invoice: Nullable<InvoiceSimplifiedDTO>) => {
    if (validateFormField("DESC_TMDOKLAD", invoice?.invoiceId)) {
      // set state
      setSelectedInvoice(invoice);
      
      // set select data to form values
      setFormValue("DESC_TMDOKLAD", invoice?.invoiceId);   
    }
  }

  // render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard title={tLabels("complaintFormSection.cardTitle")} bodyClassName="pt-4 px-4">
        <div className="row no-gutters mx-n2 position-relative">
          <div className="col-sm-6 px-2">
            <ContractSelect 
              name="DESC_TMSMLOUVA"
              contracts={contracts}
              onChange={contractChangeHandler}
              errors={errors}
              defaultContract={selectedContract}
            />
          </div>
          <div className="col-sm-6 px-2">
            <ComplaintSelect
              name="DESC_TMKATEGREKLAM"
              options={complaintTypes}
              optionName="complaintTypeName"
              optionValue="complaintTypeId"
              onChange={complaintTypeChangeHandler}
              selectedValue={selectedComplaintType}
              label={tLabels("complaintFormSection.category")}
              placeholder={tLabels("complaintFormSection.selectCategory")}
              errors={errors}
              editable={isEditableComplaintType()}
            />
          </div>
          <div className="col-sm-6 px-2">
            <ComplaintSelect
              name="DESC_TMPODKATREKLAM"
              options={complaintSubTypes}
              optionName="complaintSubTypeName"
              optionValue="complaintSubTypeId"
              onChange={complaintSubTypeChangeHandler}
              selectedValue={selectedComplaintSubType}
              label={tLabels("complaintFormSection.subCategory")}
              placeholder={tLabels("complaintFormSection.selectSubCategory")}
              errors={errors}
              editable={isEditableComplaintSubType()}
            />
          </div>
          <div className="col-sm-6 px-2">
            <ComplaintSelect
              name="DESC_TMDOKLAD"
              options={invoices}
              optionName="invoiceNumber"
              optionValue="invoiceId"
              onChange={invoiceChangeHandler}
              selectedValue={selectedInvoice}
              label={tLabels("complaintFormSection.complaintDocumentNumber")}
              placeholder={tLabels("complaintFormSection.selectDocumentNumber")}
              errors={errors}
              searchable
              editable={isEditableInvoice()}
            />
          </div>
          <PromiseLoadingSpinner area={ComplaintFormSection_Complaint} delay={100} />
        </div>       
      </PageCard>

      <BasicInfoSubFormSection
        bindField={bindField}
        bindUncontrolledField={bindUncontrolledField}
        errors={errors}
        setValidationRule={setValidationRule}
      />

      <button type="submit" className="btn btn-primary px-3 py-2">{tLabels("complaintFormSection.sendRequirement")}</button>
    </form>
  )
}

export default ComplaintFormSection;
